import type { DevicePlan, OnboardingEmailStatus, OnboardingEmailType, OnboardingTaskSource } from '../../onboarding/request.js';

export function requestStatusLabel(status: string): string {
  return status === 'REVIEW_PENDING' ? 'Review pending' : humanize(status);
}

export function devicePlanLabel(plan: DevicePlan): string {
  return {
    new_computer: 'New computer',
    existing_reassigned: 'Existing computer · reassigned',
    byod: 'Personal device (BYOD)',
  }[plan];
}

export function taskSourceLabel(source: OnboardingTaskSource): string {
  return {
    profile: 'Profile', system: 'System', requester_selection: 'Requested',
    device_plan: 'Device plan', manual_note: 'Manual',
  }[source];
}

export function emailTypeLabel(type: OnboardingEmailType): string {
  return { REQUESTER_CONFIRMATION: 'Requester confirmation', SUPPORT_TICKET: 'Support ticket' }[type];
}

export function emailStatusLabel(status: OnboardingEmailStatus): string {
  return {
    PENDING: 'Pending', IN_PROGRESS: 'In progress', SENT: 'Sent',
    FAILED: 'Failed', UNKNOWN_OUTCOME: 'Outcome unknown',
  }[status];
}

export function emailFailureLabel(reason: string): string {
  return {
    MAILER_UNAVAILABLE: 'Mail transport unavailable',
    MAILER_NOT_VERIFIED: 'Mail delivery is not verified',
    SUPPORT_MAILBOX_NOT_CONFIGURED: 'Support mailbox not configured',
  }[reason] ?? humanize(reason);
}

export function formatOnboardingDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

export function formatOnboardingTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York',
  }).format(date);
  return `${day} · ${time}`;
}

function humanize(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Unknown';
}
